import React, { useState, useEffect } from 'react'
import PageHeader from '../../../PageHeader/PageHeader'
import { getErrorMsg, hasError } from '../../../utils/utils'
import { useSelector, useDispatch } from 'react-redux';
import AppInput from '../../../components/AppInput/AppInput';
import { Navigation } from 'lucide-react';
import AppSelect from '../../../components/AppSelect/AppSelect';
import { deviceTypeOptions, horizontalResolutions, verticalResolutions, statusOptions } from '../../../constant/Options';
import AppButton from '../../../components/AppButton/AppButton';
import AppSwitch from '../../../components/Switch/AppSwitch';
import { useParams, useNavigate } from 'react-router-dom';
import { HitApi } from '../../../Api/ApiHit';
import { setApiJson, setApiErrorJson } from '../../../redux/Actions/ApiAction';
import toast from 'react-hot-toast';
import { RegisterDeviceValidation } from '../../../validationscheema/RegisterDeviceValidation';
import { registerDevice, updateDevice, searchDevice } from '../../../constant/Constant';
import { getSelectedUnit } from '../../../storage/Storage';

function RegisterDevice() {
    const ApiReducer = useSelector((state) => state.ApiReducer);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();

    const [submitLoading, setSubmitLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);

    // Get current orientation from Redux (true = vertical, false = horizontal)
    const isVertical = ApiReducer.apiJson?.isVertical || false;

    // Get resolution options based on orientation
    const getResolutionOptions = () => {
        return isVertical ? verticalResolutions : horizontalResolutions;
    };

    // Handle orientation change - reset resolution when orientation changes
    const [previousOrientation, setPreviousOrientation] = useState(isVertical);

    useEffect(() => {
        if (previousOrientation !== isVertical) {
            dispatch(setApiJson({
                ...ApiReducer.apiJson,
                resolution: ''
            }));
            setPreviousOrientation(isVertical);
        }
    }, [isVertical]);

    useEffect(() => {
        // Clear any existing errors first
        dispatch(setApiErrorJson({}));

        const initialDeviceData = {
            deviceName: '',
            deviceType: '',
            isVertical: false, // false = horizontal, true = vertical
            resolution: '',
            lat: '',
            lng: '',
            status: 'Active'
        };

        if (id) {
            setIsEditMode(true);
            loadDeviceData();
        } else {
            dispatch(setApiJson(initialDeviceData));
        }
    }, [dispatch, id]);

    const loadDeviceData = () => {
        setSubmitLoading(true);
        const json = {
            page: 1,
            limit: 10,
            search: {
                _id: id,
                unitIds: getSelectedUnit()?._id
            }
        };

        HitApi(json, searchDevice).then((res) => {
            if (res?.statusCode === 200) {
                const deviceData = res?.data?.docs?.[0];

                if (deviceData) {
                    // Convert orientation string to boolean for isVertical
                    const updatedDeviceData = {
                        ...deviceData,
                        isVertical: deviceData.orientation === 'vertical'
                    };

                    dispatch(setApiJson(updatedDeviceData));
                } else {
                    toast.error('Device not found');
                    navigate('/device-list');
                }
            } else {
                toast.error('Failed to load device data');
                navigate('/device-list');
            }
        }).catch((error) => {
            console.error('Error loading device data:', error);
            toast.error('Error loading device data. Please try again.');
            navigate('/device-list');
        }).finally(() => {
            setSubmitLoading(false);
        });
    };

    const handleSubmit = () => {
        setSubmitLoading(true);

        // Clear previous errors
        dispatch(setApiErrorJson({}));

        RegisterDeviceValidation(ApiReducer?.apiJson, isEditMode).then((errors) => {
            dispatch(setApiErrorJson(errors));

            if (Object.keys(errors).length === 0) {
                const deviceData = {
                    ...ApiReducer.apiJson,
                    orientation: isVertical ? 'vertical' : 'horizontal', // Convert boolean to string for API
                    lat: ApiReducer.apiJson.lat ? parseFloat(ApiReducer.apiJson.lat) : null,
                    lng: ApiReducer.apiJson.lng ? parseFloat(ApiReducer.apiJson.lng) : null,
                    unitIds: getSelectedUnit()?._id
                };

                if (isEditMode) {
                    deviceData._id = id;
                }

                console.log('📡 Submitting device data:', deviceData);

                const apiCall = isEditMode ? updateDevice : registerDevice;
                const successMsg = isEditMode ? 'Device updated successfully!' : 'Device registered successfully!';

                HitApi(deviceData, apiCall).then((res) => {
                    if (res?.statusCode === 200 || res?.statusCode === 201) {
                        toast.success(res.message || successMsg);
                        navigate('/registerdevice');
                    } else {
                        toast.error(res?.message || 'Failed to save device');
                    }
                }).catch((error) => {
                    console.error('Submit error:', error);
                    toast.error('Error saving device. Please try again.');
                }).finally(() => {
                    setSubmitLoading(false);
                });

            } else {
                setSubmitLoading(false);
            }
        }).catch((validationError) => {
            console.error('Validation error:', validationError);
            toast.error('Validation failed. Please check your inputs.');
            setSubmitLoading(false);
        });
    };

    return (
        <div className='p-5'>
            <PageHeader title="Register Device" description="Register your Device" />
            <div className="bg-white p-8 border rounded-xl mb-6">
                <div className="text-lg font-medium mb-5 flex justify-between items-center">
                    <span>Device Information</span>
                </div>

                <div className="grid grid-cols-1 gap-4 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <AppInput
                            title="Device Name"
                            placeholder="Device Name"
                            name="deviceName"
                            important={true}
                            error={hasError('deviceName', ApiReducer)}
                            errormsg={getErrorMsg('deviceName', ApiReducer)}
                            disabled={submitLoading}
                        />
                        <AppSelect
                            title="Device Type"
                            name="deviceType"
                            options={deviceTypeOptions}
                            important={true}
                            error={hasError('deviceType', ApiReducer)}
                            errormsg={getErrorMsg('deviceType', ApiReducer)}
                            disabled={submitLoading}
                        />
                    </div>

                    {/* Orientation Switch */}
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div className="flex-1">
                                <AppSwitch
                                    title={isVertical ? "📱 Vertical (Portrait)" : "📺 Horizontal (Landscape)"}
                                    name="isVertical"
                                    specification={isVertical
                                        ? "Switch OFF for Horizontal/Landscape orientation"
                                        : "Switch ON for Vertical/Portrait orientation"}
                                    disabled={submitLoading}
                                    activeColor="#3b82f6"
                                    inactiveColor="#e5e7eb"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Resolution Dropdown */}
                    <div className="mb-6">
                        <AppSelect
                            title={`Screen Size (${isVertical ? 'Vertical' : 'Horizontal'} Resolution)`}
                            name="resolution"
                            options={getResolutionOptions()}
                            important={false}
                            error={hasError('resolution', ApiReducer)}
                            errormsg={getErrorMsg('resolution', ApiReducer)}
                            disabled={submitLoading}
                        />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <AppInput
                            title="Latitude"
                            placeholder="e.g., 28.613917123456"
                            name="lat"
                            type="number"
                            step="0.000000000001"
                            important={false}
                            error={hasError('lat', ApiReducer)}
                            errormsg={getErrorMsg('lat', ApiReducer)}
                            icon={<Navigation size={18} />}
                            disabled={submitLoading}
                        />
                        <AppInput
                            title="Longitude"
                            placeholder="e.g., 77.209012345678"
                            name="lng"
                            type="number"
                            step="0.000000000001"
                            important={false}
                            error={hasError('lng', ApiReducer)}
                            errormsg={getErrorMsg('lng', ApiReducer)}
                            icon={<Navigation size={18} />}
                            disabled={submitLoading}
                        />
                        <AppSelect
                            title="Status"
                            name="status"
                            options={statusOptions}
                            important={true}
                            error={hasError('status', ApiReducer)}
                            errormsg={getErrorMsg('status', ApiReducer)}
                            disabled={submitLoading}
                        />
                    </div>

                    <div className='mt-5 flex justify-end'>
                        <AppButton
                            buttontext={submitLoading ? "Processing..." : (isEditMode ? "Update Device" : "Add Device")}
                            onClick={handleSubmit}
                            disabled={submitLoading}
                            isLoading={submitLoading}
                        />
                    </div>

                </div>
            </div>

        </div>
    )
}

export default RegisterDevice